import type { IdvSendTiming } from '@common/enums';
import type { LawFirm, LawFirmIntakeOptionsUpdate } from '@common/types/lawfirms';
import {
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import LexoSwitch from '@/components/LexoSwitch';
import { updateLawFirmIntakeOptions } from '@/services/LawfirmService';
import { errorSnackbar } from '@/utils/snackbar';

interface Props {
    selectedLawFirm: LawFirm;
}

interface IntakeOptionRow {
    key: Exclude<keyof LawFirmIntakeOptionsUpdate, 'defaultIdvSendTiming'>;
    label: string;
    description: string;
}

const INTAKE_OPTION_ROWS: IntakeOptionRow[] = [
    {
        key: 'showEmptyAnswersInIntakeSummary',
        label: 'Show empty answers in intake summary documents',
        description:
            'When enabled, unanswered intake questions appear as "Not Answered" in generated intake summary PDFs. When disabled, empty answers are omitted from the document.',
    },
    {
        key: 'showContactsAsColumnsInIntakeSummary',
        label: 'Show contacts side by side in intake summary documents',
        description:
            'When enabled, intake summary PDFs with more than one contact list them in columns, up to 4 per table, so their details can be compared at a glance. When disabled, each contact is listed one after another.',
    },
    {
        key: 'includeFileAttachmentsInSubmissionEmails',
        label: 'Include file attachments in submission emails',
        description:
            'When enabled, client-uploaded files and other intake documents are attached to submission notification emails sent to your firm. When disabled, no files are attached to these emails.',
    },
];

// 'Off' stands in for null, which a select can't hold as a value
const IDV_DEFAULT_OPTIONS: { value: IdvSendTiming | 'Off'; label: string }[] = [
    { value: 'Off', label: 'Off' },
    { value: 'WithIntake', label: 'With intake email' },
    { value: 'OnCompletion', label: 'On intake completion' },
];

function OptionLabel({ label, description }: { label: string; description: string }) {
    return (
        <>
            <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{label}</Typography>
            <Typography variant="body2" sx={{ color: 'neutral.N_500', mt: 0.5 }}>
                {description}
            </Typography>
        </>
    );
}

export default function IntakeOptions({ selectedLawFirm }: Props) {
    const lawFirmId = selectedLawFirm.lawFirmId;
    const queryClient = useQueryClient();

    const intakeOptions: LawFirmIntakeOptionsUpdate = {
        showEmptyAnswersInIntakeSummary: selectedLawFirm.showEmptyAnswersInIntakeSummary ?? true,
        showContactsAsColumnsInIntakeSummary:
            selectedLawFirm.showContactsAsColumnsInIntakeSummary ?? false,
        includeFileAttachmentsInSubmissionEmails:
            selectedLawFirm.includeFileAttachmentsInSubmissionEmails ?? true,
        defaultIdvSendTiming: selectedLawFirm.defaultIdvSendTiming ?? null,
    };

    const mutation = useMutation({
        mutationFn: (nextOptions: LawFirmIntakeOptionsUpdate) =>
            updateLawFirmIntakeOptions(lawFirmId, nextOptions),
        onMutate: async (nextOptions) => {
            await queryClient.cancelQueries({ queryKey: ['lawFirm', lawFirmId] });

            const previous = queryClient.getQueryData<LawFirm>(['lawFirm', lawFirmId]);

            queryClient.setQueryData<LawFirm>(['lawFirm', lawFirmId], (old) =>
                old ? { ...old, ...nextOptions } : old,
            );

            return { previous };
        },
        onError: (_err, _nextOptions, context) => {
            if (context?.previous) {
                queryClient.setQueryData(['lawFirm', lawFirmId], context.previous);
            }
            errorSnackbar('Failed to update intake options');
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['lawFirm', lawFirmId] });
        },
    });

    return (
        <Stack spacing={2}>
            <Typography variant="h1">Intake Options</Typography>
            <Table size="small" sx={{ width: '100%', maxWidth: '600px' }}>
                <TableBody>
                    {INTAKE_OPTION_ROWS.map((row) => (
                        <TableRow key={row.key}>
                            <TableCell sx={{ paddingLeft: 0 }}>
                                <OptionLabel label={row.label} description={row.description} />
                            </TableCell>
                            <TableCell align="right" sx={{ paddingRight: 0, verticalAlign: 'top' }}>
                                <LexoSwitch
                                    checked={intakeOptions[row.key]}
                                    onChange={(event) => {
                                        if (mutation.isPending) return;
                                        mutation.mutate({
                                            ...intakeOptions,
                                            [row.key]: event.target.checked,
                                        });
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell sx={{ paddingLeft: 0 }}>
                            <OptionLabel
                                label="Send ID verification automatically"
                                description="Whether clients added in New Prospect start with ID verification sent automatically, and when. Staff can still change it for each client. When on, each client needs a Canadian cell number unless it's turned off for them. Treefort ID verification is an additional charge per client."
                            />
                        </TableCell>
                        <TableCell align="right" sx={{ paddingRight: 0, verticalAlign: 'top' }}>
                            <TextField
                                select
                                size="small"
                                value={intakeOptions.defaultIdvSendTiming ?? 'Off'}
                                onChange={(event) => {
                                    if (mutation.isPending) return;
                                    const value = event.target.value as IdvSendTiming | 'Off';
                                    mutation.mutate({
                                        ...intakeOptions,
                                        defaultIdvSendTiming: value === 'Off' ? null : value,
                                    });
                                }}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'Send ID verification automatically',
                                    },
                                }}
                                sx={{ minWidth: 220, textAlign: 'left' }}
                            >
                                {IDV_DEFAULT_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Stack>
    );
}
